import { FormInput, TabsNavigation } from 'components/molecules';
import TopBar from 'components/organisms/top-bar/TopBar';
import { TopbarTabs } from 'components/templates';
import { Form, Formik, FormikConfig } from 'formik';
import i18next from 'i18next';
import { useTranslation } from 'react-i18next';
import { IClientAccountForm } from 'services/client-account/types';
import { useGetLabelLightList } from 'services/labels/light';
import { II18nextPrefix } from 'types/components/components.interface';
import { array, object, string } from 'yup';
import { IFormikProps, IFormProps } from '../types';
import ClientAccountArtists from './tabs/ClientAccountArtists';
import ClientAccountChannels from './tabs/ClientAccountChannels';
import ClientAccountDigitalMusicProducts from './tabs/ClientAccountDigitalMusicProduct';
import ClientAccountMerch from './tabs/ClientAccountMerch';
import ClientAccountPhysicalMusicProducts from './tabs/ClientAccountPhysicalMusicProducts';
import ClientAccountTracks from './tabs/ClientAccountTracks';
import ClientAccountUsers from './tabs/ClientAccountUsers';
import ClientAccountVideo from './tabs/ClientAccountVideo';

export enum ClientAccountTabs {
  users,
  artists,
  tracks,
  video,
  digital,
  physical,
  merch,
  channel,
}

export const clientAccountInitialValues: IClientAccountForm = {
  name: '',
  label: { id: '', name: '' },
  users: [],
  artists: [],
  tracks: [],
  digitalMusicProducts: [],
  physicalMusicProducts: [],
  merch: [],
  channels: [],
  videos: [],
};

export const clientAccountEntityValidator = object().shape({
  id: string().required(() => i18next.t('formik-error.required')),
});

export const clientAccountValidator = object().shape({
  label: clientAccountEntityValidator,
  users: array().of(clientAccountEntityValidator),
  artists: array().of(clientAccountEntityValidator),
  tracks: array().of(clientAccountEntityValidator),
  digitalMusicProducts: array().of(clientAccountEntityValidator),
  physicalMusicProducts: array().of(clientAccountEntityValidator),
  merch: array().of(clientAccountEntityValidator),
  channels: array().of(clientAccountEntityValidator),
  videos: array().of(clientAccountEntityValidator),
});

export interface IClientAccountFormProps
  extends FormikConfig<IClientAccountForm>,
    IFormProps,
    II18nextPrefix {}

const ClientAccountForm = ({
  tittle,
  customBackItem,
  backPath,
  topbarContent,
  initialValues = clientAccountInitialValues,
  disableTabs,
  status,
  ...props
}: IClientAccountFormProps) => {
  const { t } = useTranslation();

  const labelLightList = useGetLabelLightList();

  const labelOptions = labelLightList.data?.map(label => ({
    label: label.labelName,
    value: label.id,
  }));

  return (
    <Formik
      {...props}
      validationSchema={clientAccountValidator}
      validateOnChange={true}
      validateOnBlur={true}
      initialStatus={status}
      enableReinitialize
      initialValues={initialValues}>
      {({
        values,
        status,
        errors,
        setFieldValue,
      }: IFormikProps<IClientAccountForm>) => (
        <Form className='w-full h-full'>
          <TopbarTabs>
            <TopBar
              title={status?.displayMode ? values.label.name : undefined}
              customBackItem={customBackItem}
              path={backPath ?? 'client-account'}
              topbarContent={
                <div className='flex w-full items-center justify-end'>
                  <div className='flex w-full'>
                    {!status?.displayMode && (
                      <FormInput.Select
                        name={`label.id`}
                        isAsync
                        isSearchable
                        label={t(`input.label.label`)}
                        onChange={(value, actionMeta) => {
                          setFieldValue(`label.name`, value.label);
                        }}
                        value={{
                          label: values.label.name,
                          value: values.label.id,
                        }}
                        options={labelOptions}
                        isLoading={labelLightList.isLoading}
                      />
                    )}
                  </div>
                  <div className='flex w-max'>{topbarContent}</div>
                </div>
              }>
              <TabsNavigation
                tabs={[
                  {
                    value: ClientAccountTabs.users,
                    name: 'users',
                    error: errors.users,
                  },
                  {
                    value: ClientAccountTabs.artists,
                    name: 'artists',
                    error: errors.artists,
                  },
                  {
                    value: ClientAccountTabs.tracks,
                    name: 'tracks',
                    error: errors.tracks,
                  },
                  {
                    value: ClientAccountTabs.video,
                    name: 'video',
                    error: errors.videos,
                  },
                  {
                    value: ClientAccountTabs.digital,
                    name: 'digital',
                    error: errors.digitalMusicProducts,
                  },
                  {
                    value: ClientAccountTabs.physical,
                    name: 'physical',
                    error: errors.physicalMusicProducts,
                  },
                  {
                    value: ClientAccountTabs.merch,
                    name: 'merch',
                    error: errors.merch,
                  },
                  {
                    value: ClientAccountTabs.channel,
                    name: 'channel',
                    error: errors.channels,
                  },
                ]}
                i18nextPrefix={'client-account'}
                disabled={disableTabs}
              />
            </TopBar>
          </TopbarTabs>
        </Form>
      )}
    </Formik>
  );
};

ClientAccountForm.getRouterTabs = (disabledChildren?: string[]) =>
  [
    { path: 'users', element: <ClientAccountUsers /> },
    { path: 'artists', element: <ClientAccountArtists /> },
    { path: 'tracks', element: <ClientAccountTracks /> },
    { path: 'video', element: <ClientAccountVideo /> },
    { path: 'digital', element: <ClientAccountDigitalMusicProducts /> },
    { path: 'physical', element: <ClientAccountPhysicalMusicProducts /> },
    { path: 'merch', element: <ClientAccountMerch /> },
    { path: 'channel', element: <ClientAccountChannels /> },
  ].filter(children => !disabledChildren?.includes(children.path));

export default ClientAccountForm;
